/**
 * Projects Page Component
 * 
 * Page for managing projects. Each project defines a callback URL
 * where parsed transaction data is sent.
 * 
 * Features:
 * - View all projects in a table with callback URL and account counts
 * - Search projects by Name or Callback URL
 * - One-click copy for Callback URL
 * - Direct "View Accounts" button linking to the Bank Management page filtered by project
 * - Create new projects with name and callback URL
 * - Delete projects
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Alert,
  Tag,
  Tooltip,
  Space,
  Typography,
} from 'antd';
import {
  SearchOutlined,
  CopyOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  LinkOutlined,
  ProjectOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getProjectList, createProject, deleteProject, getBankList } from '../../services/api';
import styles from './ProjectsPage.module.css';

const { Text } = Typography;

const ProjectsPage = () => {
  const navigate = useNavigate();

  // State management
  const [projects, setProjects] = useState([]); // Project list
  const [bankAccounts, setBankAccounts] = useState([]); // Bank accounts for count calculation
  const [loading, setLoading] = useState(false); // Loading state
  const [error, setError] = useState(null); // Error message
  const [isModalVisible, setIsModalVisible] = useState(false); // Create modal visibility
  const [searchTerm, setSearchTerm] = useState(''); // Search filter
  const [form] = Form.useForm(); // Ant Design form instance

  /**
   * Fetches all projects and bank accounts from the API.
   */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [projRes, bankRes] = await Promise.allSettled([
        getProjectList(),
        getBankList({ payout_system: 0 }),
      ]);

      if (projRes.status === 'fulfilled') {
        setProjects(projRes.value.data || []);
      } else {
        setError(projRes.reason?.response?.data?.detail || 'Error loading projects');
      }

      if (bankRes.status === 'fulfilled') {
        setBankAccounts(bankRes.value.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading data');
    }
    setLoading(false);
  };

  /**
   * Handles project creation form submission.
   */
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await createProject(values);
      message.success('Project created successfully.');
      fetchData();
      setIsModalVisible(false);
      form.resetFields();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Error creating project.');
    }
  };

  /**
   * Handles project deletion.
   * @param {number} projectId - ID of the project to delete
   */
  const handleDelete = async (projectId) => {
    try {
      await deleteProject(projectId);
      message.success('Project deleted successfully.');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Error deleting project.');
    }
  };

  /**
   * Copies text to clipboard with notification.
   */
  const handleCopy = (text, label = 'Text') => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      message.success(`${label} copied to clipboard!`);
    }
  };

  // Fetch projects on component mount
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Map of project ID to account count.
   */
  const projectAccountStats = useMemo(() => {
    const stats = {};
    bankAccounts
      .filter((acc) => !acc.payout_system)
      .forEach((acc) => {
        const pId = acc.project_id;
        if (pId) {
          if (!stats[pId]) stats[pId] = { total: 0, working: 0 };
          stats[pId].total += 1;
          if (acc.status === 'working') stats[pId].working += 1;
        }
      });
    return stats;
  }, [bankAccounts]);

  /**
   * Filtered projects based on search term.
   */
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects;
    const term = searchTerm.toLowerCase().trim();
    return projects.filter(
      (p) =>
        p.name?.toLowerCase().includes(term) ||
        p.callback_url?.toLowerCase().includes(term) ||
        String(p.id).includes(term)
    );
  }, [projects, searchTerm]);

  // Table column definitions
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <Tag color="blue">#{id}</Tag>,
    },
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space orientation="horizontal" size={6}>
          <ProjectOutlined style={{ color: '#1677ff' }} />
          <Text orientation="horizontal" strong style={{ fontSize: 14 }}>
            {name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Callback URL',
      dataIndex: 'callback_url',
      key: 'callback_url',
      render: (url) => {
        if (!url) return <Text type="secondary" italic>None</Text>;
        return (
          <Space orientation="horizontal" size={8} style={{ maxWidth: '100%' }}>
            <Tooltip title={url}>
              <Tag
                style={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  maxWidth: 350,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              >
                <LinkOutlined style={{ marginRight: 4 }} />
                {url}
              </Tag>
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              title="Copy URL"
              onClick={() => handleCopy(url, 'Callback URL')}
            />
          </Space>
        );
      },
    },
    {
      title: 'Accounts',
      key: 'accounts_count',
      width: 140,
      render: (record) => {
        const stat = projectAccountStats[record.id] || { total: 0, working: 0 };
        return (
          <Space orientation="horizontal" size={6}>
            <Tag color={stat.total > 0 ? 'geekblue' : 'default'}>
              {stat.total} {stat.total === 1 ? 'account' : 'accounts'}
            </Tag>
            {stat.working > 0 && (
              <Tag color="green">
                {stat.working} working
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (record) => (
        <Space orientation="horizontal" size={8}>
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/?project_id=${record.id}`)}
          >
            View Accounts
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Delete Project',
                content: `Are you sure you want to delete project "${record.name}"? Accounts associated with this project may be affected.`,
                okText: 'Yes, Delete',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => handleDelete(record.id),
              });
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Project Management</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className={styles.addButton}
          onClick={() => setIsModalVisible(true)}
        >
          Add Project
        </Button>
      </div>

      {/* Search and stats bar */}
      <div className={styles.toolbar}>
        <Input
          placeholder="Search projects by name, callback URL, or ID..."
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 400 }}
        />
        <div style={{ color: '#888', fontSize: 13 }}>
          Total Projects: <strong>{filteredProjects.length}</strong>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <Alert
          className={styles.errorAlert}
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
        />
      )}

      {/* Projects table */}
      <Table
        dataSource={filteredProjects}
        columns={columns}
        loading={loading}
        rowKey="id"
        className={styles.table}
        pagination={{ pageSize: 15 }}
      />

      {/* Create project modal */}
      <Modal
        title="Add Project"
        visible={isModalVisible}
        onOk={handleCreate}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical" className={styles.modalForm}>
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter the project name' }]}
          >
            <Input placeholder="Enter project name (e.g. Amazon Pay)" />
          </Form.Item>
          <Form.Item
            name="callback_url"
            label="Callback URL"
            rules={[
              { required: true, message: 'Please enter the callback URL' },
              {
                type: 'url',
                message: 'Please enter a valid URL (e.g. https://api.example.com/callback)',
              },
            ]}
          >
            <Input placeholder="Enter callback URL" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectsPage;
